'use client';

import { useTranslations } from 'next-intl';
import { useContext, useState } from 'react';

import { Button, Typography, Input } from '@/components/atoms';
import { Sheet } from '@/components/molecules';
import { useRouter } from '@/i18n/routing';
import { FavoritesContext } from '@/lib/contexts/favouriting';

import { ApprovalModal } from './ApprovalModal';
import { OrSeparator } from './OrSeparator';

export interface PlaylistOptionsMenuProps {
  drawerOpen: boolean;
  drawerOpenAction: (open: boolean) => void;
  playlistTitle: string;
  playlistId: string | number;
  onRename?: (newName: string) => void;
}

export const PlaylistOptionsMenu = ({
  drawerOpen,
  drawerOpenAction,
  playlistTitle,
  playlistId,
  onRename,
}: PlaylistOptionsMenuProps) => {
  const t = useTranslations();
  const router = useRouter();
  const [newPlaylistName, setNewPlaylistName] = useState(playlistTitle);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const favoritesContext = useContext(FavoritesContext);
  if (!favoritesContext) {
    throw new Error('Add menu must be wrapped in provider');
  }
  const {
    deleteUserPlaylistAndStore,
    renameUserPlaylistAndStore,
    fetchUserPlaylists,
  } = favoritesContext;
  const handleRenamePlaylist = async () => {
    if (
      !newPlaylistName ||
      newPlaylistName === '' ||
      newPlaylistName === playlistTitle
    )
      return;
    await renameUserPlaylistAndStore(Number(playlistId), newPlaylistName);
    onRename?.(newPlaylistName);
    drawerOpenAction(false);
  };
  const handleDeletePlaylist = async () => {
    setShowDeleteModal(true);
    drawerOpenAction(false);
  };
  const confirmDeletePlaylist = async () => {
    await deleteUserPlaylistAndStore(Number(playlistId));
    await fetchUserPlaylists();
    drawerOpenAction(false);
    setShowDeleteModal(false);
    router.push(`/library`);
  };
  return (
    <>
      <Sheet.Root
        isOpen={drawerOpen}
        onOpenChange={drawerOpenAction}
        aria-labelledby="test"
        className="flex flex-col"
      >
        <Sheet.Header>
          <Sheet.Title>{t('MORE_OPTIONS')}</Sheet.Title>
          <Sheet.Description className="sr-only">
            {t('MORE_OPTIONS')}
          </Sheet.Description>
          <Sheet.Close />
        </Sheet.Header>
        <Sheet.Body className="flex-1 overflow-auto gap-2">
          <Typography.Text variant="paragraph" size="md" weight="medium">
            {t('AUTH_SIGNUP_BSO_TITLE')}
          </Typography.Text>
          <Input
            value={newPlaylistName}
            onChange={e => {
              setNewPlaylistName(e.target.value);
            }}
            variant={'default'}
            className="w-full mt-2 mb-2"
          />
          <Button
            size="sm"
            variant="primary"
            className="w-full mt-2"
            onClick={handleRenamePlaylist}
          >
            {t('RENAME_PLAYLIST_TITLE')}
          </Button>
          <OrSeparator />
          <Typography.Text variant="paragraph" size="lg" weight="medium">
            {t('DELETE_PLAYLIST')} ?
          </Typography.Text>
          <div className="flex flex-row items-center justify-around mt-2">
            <Button
              size="sm"
              variant="primary"
              className="mt-2 w-2/5 text-center"
              onClick={() => drawerOpenAction(false)}
            >
              {t('MOBILE_WEBPLAYER_CANCEL')}
            </Button>
            <Button
              size="sm"
              variant="error"
              className="mt-2 w-2/5 text-center"
              onClick={handleDeletePlaylist}
            >
              {t('DELETE_PLAYLIST')}
            </Button>
          </div>
        </Sheet.Body>
      </Sheet.Root>
      {showDeleteModal && (
        <ApprovalModal
          modalOpen={showDeleteModal}
          modalOpenAction={setShowDeleteModal}
          modalTitle={t('EXPANDED_PLAYER_SETTINGS')}
          modalSubtitle={t('DELETE_PLAYLIST')}
          modalCTA={t('DELETE_PLAYLIST')}
          onConfirm={confirmDeletePlaylist}
        />
      )}
    </>
  );
};
